import { API_BASE_URL } from "../config/api";
import { clearAuthenticated } from "../utils/authSession";

interface RequestOptions extends RequestInit {
    endpoint: string;
}

interface ApiError {
    message?: string;
}

async function parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");

    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        // Handle expired or invalid token globally
        if (response.status === 401 || response.status === 403) {
            if (typeof window !== "undefined") {
                try {
                    clearAuthenticated();
                } catch (e) {
                    // ignore
                }
                // Redirect to login (replace to avoid back navigation)
                window.location.replace('/login?mode=signup');
            }

            throw new Error('Session expired. Please login again.');
        }

        const errorPayload = payload as ApiError | string;
        const message =
            (typeof errorPayload === "object" && errorPayload?.message) ||
            (typeof errorPayload === "string" && errorPayload) ||
            `Request failed with status ${response.status}`;

        throw new Error(message);
    }

    return payload as T;
}

export async function apiRequest<T>({ endpoint, ...options }: RequestOptions): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...(options.headers || {}),
        },
    });

    return parseResponse<T>(response);
}
